import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningPage from './WarningPage';
import useWarningMonth from './useWarningMonth';
import RetryNotice from './RetryNotice';
import { departmentLabel } from './departments';
import WarningLoading from './WarningLoading';
import apiRequest from '../../api/request';
import WarningMetricTable from './WarningMetricTable';
import FlagSummary from './FlagSummary';
import ThresholdNote from './ThresholdNote';
import useTutorCountDrilldown from './useTutorCountDrilldown';
import useConfigAccess from './useConfigAccess';

const DEPARTMENTS = [
  { id: 11, name: 'OGD' },
  { id: 12, name: 'CSD' },
  { id: 3, name: 'Foundation' },
  { id: 20, name: 'Classroom' },
  { id: 21, name: 'Little Genie' },
  { id: 22, name: 'Speak Pro' },
  { id: 35, name: 'SkillX' },
];
const departmentName = (id) => departmentLabel(id, DEPARTMENTS);

const StatusCell = ({ row }) => {
  if (row.isBatch) return null; // the batch (group-class) line is not a CRO
  if (row.isWarning) return <WarningAmberIcon fontSize="small" sx={{ color: 'error.main', display: 'block', mx: 'auto' }} />;
  if (row.threshold === null) return <Typography variant="body2" color="text.disabled" align="center">-</Typography>; // no threshold for the department: not judged
  return <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main', display: 'block', mx: 'auto' }} />;
};

const statusText = (row) => (row.isBatch ? '' : row.isWarning ? 'Warning' : row.threshold === null ? 'Not configured' : 'OK');

const tutorColumns = (drill) => [
  { field: 'status', headerName: 'Status', align: 'center', render: (row) => <StatusCell row={row} />, csvValue: statusText },
  {
    field: 'entityName',
    headerName: 'CRO',
    render: (row) =>
      row.isBatch ? (
        <Typography variant="body2" fontWeight={700} color="primary.main">{row.entityName}</Typography>
      ) : (
        <Typography variant="body2" fontStyle={row.isActive === false ? 'italic' : 'normal'} color={row.isActive === false || row.isWarning ? 'error.main' : 'text.primary'}>
          {row.entityName}{row.isActive === false && ' (Inactive)'}
        </Typography>
      ),
  },
  { field: 'departmentId', headerName: 'Department', render: (row) => departmentName(row.departmentId), csvValue: (row) => departmentName(row.departmentId) },
  // the tutors this CRO is handling NOW: active assignments on the CRO's active course enrollments, as of the month's end. A tutor
  // shared by several rows is in each of them, so this column does NOT add up to the card. Click the number for the tutors.
  { field: 'activeTutors', headerName: 'Tutors handling (at month end)', align: 'right', render: drill.cell('active', 'activeTutors') },
  // of those, the tutors whose assignment to this CRO's students was made in the month
  { field: 'newTutors', headerName: 'Assigned this month', align: 'right', render: drill.cell('new', 'newTutors') },
  {
    field: 'threshold',
    headerName: 'Threshold',
    align: 'right',
    // the department's number: a CRO handling fewer tutors than this is flagged
    render: (row) => (row.isBatch ? null : row.threshold === null
      ? <Typography variant="body2" color="text.secondary">{row.thresholdOff ? 'Switched off' : 'Not configured'}</Typography>
      : `< ${row.threshold} tutors`),
    csvValue: (row) => (row.isBatch ? '' : row.threshold === null ? (row.thresholdOff ? 'Switched off' : 'Not configured') : `< ${row.threshold} tutors`),
  },
  // the same tutors, each given to ONE CRO (the one whose students they teach most) - this column adds up
  { field: 'countedOnce', headerName: 'Counted once (adds up to the card)', align: 'right' },
];

const fmt = (n) => Number(n || 0).toLocaleString('en-IN');

// Why the card and the table differ (BUG: card 2,241 vs rows summing to 4,185). Both are right: the card
// counts every tutor once, company-wide; a row counts the tutors of ONE CRO's students, so a tutor who
// serves several CROs is in each of those rows. The numbers come from the backend (`reconciliation`) so
// this always matches what is on screen.
const Reconciliation = ({ activeTutors, reconciliation: r }) => (
  <Alert severity="info" icon={false} sx={{ mt: 2, maxWidth: 760, '& .MuiAlert-message': { width: '100%' } }}>
    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>How the card and the table below add up</Typography>
    {!r.scoped ? (
      <>
        <Typography variant="body2" sx={{ mb: 0.75 }}>
          <strong>{fmt(r.tutorsInRows)}</strong> tutors counted under a CRO + <strong>{fmt(r.tutorsWithoutCro)}</strong> teaching only batch (group)
          classes (no single CRO) = <strong>{fmt(activeTutors)}</strong> active tutors, the card.
        </Typography>
        {r.batchTutorsTeaching > 0 && (
          <Typography variant="body2" sx={{ mb: 0.75 }}>
            The <strong>Batch</strong> row lists the {fmt(r.batchTutorsTeaching)} tutors who teach batch classes: {fmt(r.tutorsWithoutCro)} teach only batches
            (counted there), and {fmt(r.batchTutorsTeaching - r.tutorsWithoutCro)} also teach a CRO's students (counted under that CRO).
          </Typography>
        )}
      </>
    ) : (
      <Typography variant="body2" sx={{ mb: 0.75 }}>
        The card counts all {fmt(activeTutors)} tutors in the company, not only your CROs. Your CROs' tutors, counted once, are{' '}
        <strong>{fmt(r.countedOnceTotal)}</strong>.
      </Typography>
    )}
    <Typography variant="body2">
      <strong>Counted once</strong> gives each tutor to the one CRO whose students they teach most, so that column adds up.{' '}
      <strong>Tutors handling</strong> counts a shared tutor under every CRO they serve
      {r.countedMoreThanOnce > 0 ? `, which is why the CRO rows total ${fmt(r.rowsTotal)} (${fmt(r.tutorsOnMultipleCros)} tutors serve 2 or more CROs).` : '.'}
    </Typography>
  </Alert>
);

// The Batch (group-class) line, as a row of its own beside the CROs: teaching = every tutor with a batch
// class, counted once = the batch-only tutors (the rest are counted under their CRO). Not shown to a scoped
// viewer - batch classes belong to no single CRO, so they are not "theirs".
const batchRowFor = (r) =>
  !r.scoped && r.batchTutorsTeaching > 0
    ? {
        entityId: 'batch',
        entityName: 'Batch (group) classes - no single CRO',
        isBatch: true,
        excludeFromCount: true, // a line of the table, not a CRO - the "N of M shown" count is of CROs
        departmentId: null,
        activeTutors: r.batchTutorsTeaching,
        countedOnce: r.tutorsWithoutCro,
      }
    : null;

// Totals under the table. Sums follow the table's search/department filter. With nothing filtered and nothing
// scoped, the second line states the card next to the total so the equality is visible in the table itself.
const buildFooter = (activeTutors, r) => (shown, all) => {
  const sumOnce = shown.reduce((t, row) => t + (row.countedOnce || 0), 0);
  const sumTeaching = shown.reduce((t, row) => t + (row.activeTutors || 0), 0);
  const unfiltered = shown.length === all.length;
  const exact = !r.scoped && unfiltered;
  const out = [
    {
      key: 'total',
      first: true,
      cells: {
        entityName: exact ? 'Total of all rows above' : unfiltered ? 'Total (your view)' : 'Total of the rows shown',
        activeTutors: fmt(sumTeaching),
        countedOnce: fmt(sumOnce),
      },
    },
  ];
  if (exact) {
    out.push({ key: 'card', emphasis: true, cells: { entityName: 'All active tutors (the card above)', activeTutors: '', countedOnce: fmt(activeTutors) } });
  }
  return out;
};

// Tutor Count (Requirement Document v1.0 S4.26), modelled on MAU: the company-wide Active Tutors card (click it for every active
// tutor), and the CROs handling tutors - each with the tutors handling now and the tutors assigned this month (click either number
// for the tutors) and a per-department threshold (a CRO handling fewer tutors than their department's number is flagged). Tutor
// Count used to be a "No Threshold" informational snapshot.
const TutorCountDashboard = () => {
  // The month is shared across the whole dashboard and remembered for the session (useWarningMonth.js).
  const [month, setMonth] = useWarningMonth();
  const drill = useTutorCountDrilldown(month); // a click on a tutor count (or the card) opens the tutors behind it
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0); // Retry re-runs just this request

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiRequest({ url: `warning-dashboard/tutor-count-dashboard?month=${month.format('YYYY-MM-DD')}`, method: 'get' })
      .then((res) => {
        if (cancelled) return;
        // apiRequest resolves even on a failed call (src/api/request.js) - without this check a server error
        // rendered as a blank page instead of a message
        if (res?.status !== 'success') {
          setData(null);
          setError(res?.message || 'Could not load Tutor Count');
          return;
        }
        setData(res?.data || null);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Could not load Tutor Count');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month, reloadKey]);

  // flagged CROs first, the furthest below their department's number at the top; then the biggest; the batch line stays last
  const sortedRows = useMemo(() => {
    const cros = [...(data?.rows || [])].sort((a, b) =>
      (Number(b.isWarning) - Number(a.isWarning))
      || ((b.threshold === null ? -Infinity : b.threshold - b.activeTutors) - (a.threshold === null ? -Infinity : a.threshold - a.activeTutors))
      || (b.activeTutors - a.activeTutors));
    const batch = data?.reconciliation && batchRowFor(data.reconciliation);
    return batch ? [...cros, batch] : cros;
  }, [data]);

  const { canConfigure } = useConfigAccess();

  return (
    <WarningPage
      title="Tutor Count"
      actions={
        <Box display="flex" gap={1}>
          <Button component={Link} to={`/dashboard/warning-dashboard?month=${month.format('YYYY-MM-DD')}`} variant="text">
            Back to Warning Dashboard
          </Button>
          {canConfigure('tutor-count-config') && (
            <Button component={Link} to={`/dashboard/warning-dashboard/tutor-count-config?month=${month.format('YYYY-MM-DD')}`} variant="outlined">
              Configure Threshold
            </Button>
          )}
        </Box>
      }
    >
      <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker views={['year', 'month']} label="Month" value={month} onChange={(v) => v && setMonth(v)} />
        </LocalizationProvider>
      </Box>

      {loading && <WarningLoading />}

      {!loading && error && <RetryNotice message={error} onRetry={() => setReloadKey((k) => k + 1)} />}

      {!loading && !error && data && (
        <Paper
          elevation={2}
          component="button"
          type="button"
          onClick={drill.openAll}
          onMouseEnter={drill.prefetchAll}
          onFocus={drill.prefetchAll}
          aria-label={`Show all ${data.activeTutors} active tutors`}
          title="Show every active tutor"
          sx={{ p: 3, minWidth: 260, maxWidth: 320, textAlign: 'left', border: 'none', cursor: 'pointer', font: 'inherit', color: 'inherit', background: 'inherit', display: 'block' }}
        >
          <Typography variant="subtitle2" color="text.secondary">Active Tutors</Typography>
          <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>{data.activeTutors}</Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            As of {dayjs(data.asOfDate).format('DD MMM YYYY')} - on active enrollments and group classes
          </Typography>
          <Typography variant="caption" color="primary" component="div" sx={{ mt: 0.5 }}>Click to see all the tutors</Typography>
        </Paper>
      )}

      {!loading && !error && data && data.reconciliation && (
        <Reconciliation activeTutors={data.activeTutors} reconciliation={data.reconciliation} />
      )}

      {!loading && !error && data && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1">By CRO - the tutors each CRO is handling</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Only CROs handling at least one tutor. A CRO handling fewer tutors than their department's threshold is flagged. Click a number to see the tutors.
          </Typography>
          <ThresholdNote note={data.thresholdNote} month={month} configPath="tutor-count-config" canSet={canConfigure('tutor-count-config')} />
          <FlagSummary warningCount={data.warningCount || 0} totalEvaluated={data.totalEvaluated || 0} unit=" CROs" />

          <WarningMetricTable
            rows={sortedRows}
            columns={tutorColumns(drill)}
            departmentName={departmentName}
            emptyMessage="No tutors attributed to a CRO this period, in your scope."
            exportFileName="Tutor_Count_By_CRO"
            footer={data.reconciliation ? buildFooter(data.activeTutors, data.reconciliation) : undefined}
          />
        </Box>
      )}
      {drill.dialog}
    </WarningPage>
  );
};

export default TutorCountDashboard;
